import type { KeywordPin } from './types';

export const GAME_PLAN_PIN_ID = 'kp1';

/**
 * Built-in keyword pins. These live in code (not on the server): the
 * server-backed store only holds custom pins and *overrides* of these built-ins
 * (a disable toggle or a content edit).
 *
 * All four are `builtin: true`, meaning the UI hides the delete button and
 * forbids editing `label`/`keyword`. Other fields (color, prompt, trigger,
 * enabled) remain user-editable. Changes here propagate to existing users on
 * next load via `mergeKeywordPins` (untouched built-ins are reseeded; an
 * override whose Label no longer matches any default is dropped).
 */
export const DEFAULT_KEYWORD_PINS: ReadonlyArray<KeywordPin> = [
	{
		id: GAME_PLAN_PIN_ID,
		label: 'Game Plan',
		keyword: 'game plan',
		color: '#EA6A2C',
		prompt: 'Send me the game plan from the prompt for this lead',
		trigger: 'before_call',
		enabled: true,
		builtin: true,
	},
	{
		// "Who am I calling" — names, nicknames, pronunciation, pronouns. Fires
		// `on_dial` (not `before_call`) so it doesn't stack a second auto-send
		// alongside "Game Plan" before the call is placed.
		id: 'kp5',
		label: 'Name Check',
		// Also typed-triggerable. Auto-fires `on_dial` regardless of keyword;
		// the keyword lets a rep re-fire it mid-call by typing it.
		keyword: 'name check',
		color: '#EC4899',
		prompt:
			'For each contact on this lead, give me as a single bullet with their full name as the header, then on separate indented lines: 1) nickname only if one is clearly noted in transcripts, lead context, or prior conversations (omit this line entirely if none), 2) phonetic pronunciation using simple phonetic spelling only (no IPA symbols), with first and last name separated by a pipe (e.g., "fah-bee-AH-nah | SHNY-der"), 3) likely pronouns based on name and lead context. Flag uncertainty inline, not in a separate section. Do not split first and last name into separate bullets. No summary, no notes section, no follow-up offers. Keep it tight.',
		trigger: 'on_dial',
		enabled: true,
		builtin: true,
	},
	{
		// "Why do I have credibility" — partnership / ecosystem hooks for the
		// opener. Pre-call prep, so it fires `before_call`.
		id: 'kp6',
		label: 'Foot in the Door',
		// Also typed-triggerable. Auto-fires `before_call` regardless of keyword.
		keyword: 'foot in the door',
		color: '#3B82F6',
		prompt:
			"Based on this lead's vertical, location, and any available context, surface any relevant Levitate partnerships, integrations, carrier relationships, industry affiliations, or association memberships that could be used as a credibility hook in the opener. Include any \"we work with [name they'd recognize]\" angles or shared ecosystem connections. Only surface what's real and verifiable from lead context and knowledge docs. If nothing relevant exists, say so in one line. No filler, no summary, no follow-up offers, no disclaimers about what wasn't found. Only show what IS there. Keep it scannable.",
		trigger: 'before_call',
		enabled: true,
		builtin: true,
	},
	{
		id: 'kp4',
		label: 'Give me a pitch',
		keyword: 'give me a pitch',
		color: '#10A37F',
		prompt:
			'Give me a tight 30-second pitch tailored to this prospect: who we are, why it matters to them specifically, and the single CTA I should push for.',
		trigger: 'starter',
		enabled: true,
		builtin: true,
	},
];

/**
 * Splits the enabled `before_call` pins for a fresh conversation. The Game Plan pin, when enabled,
 * supplies the text of the opening auto-start turn; the rest are sent after that turn's reply.
 */
export const splitBeforeCallPins = (
	pins: ReadonlyArray<KeywordPin>
): { gamePlan: KeywordPin | undefined; others: KeywordPin[] } => {
	const beforeCall = pins.filter(pin => pin.enabled && pin.trigger === 'before_call');
	return {
		gamePlan: beforeCall.find(pin => pin.id === GAME_PLAN_PIN_ID),
		others: beforeCall.filter(pin => pin.id !== GAME_PLAN_PIN_ID),
	};
};
